import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Agreement, type CompanyCard as Card, type CompanySummary } from '../api';
import { date, money, nip, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';
import { IndividualAgreements } from './IndividualAgreements';

/**
 * Firmy z zewnątrz (E11-S7).
 *
 * Operator widzi, kto firmę prowadzi i jak się rozlicza — nie widzi ksiąg, faktur ani
 * dokumentów. To dane klienta; panel służy obsłudze rozliczeń, nie prowadzeniu księgowości
 * za kogoś. Wyszukiwanie po NIP-ie albo nazwie, bo operator wpisuje to, co ma przed oczami.
 */

function settlement(company: CompanySummary, t: Dictionary): string {
  if (!company.taxationForm) return t.companies.noSettings;
  return [
    t.companies.taxation[company.taxationForm],
    company.booksType ? t.companies.books[company.booksType] : null,
    company.vatStatus ? t.companies.vat[company.vatStatus] : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function Companies({
  t,
  onOpen,
}: {
  t: Dictionary;
  onOpen: (companyId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<CompanySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lista pokazuje się od razu, bez wpisywania; zapytanie zawęża ją po chwili zwłoki
  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .companies(query)
        .then(setItems)
        .catch((failure) => setError(failure instanceof ApiError ? failure.message : String(failure)));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.companies.title}</h3>
          <p>{t.companies.lead}</p>
        </div>
      </div>

      <div className="panel">
        <label className="fld">
          <span>{t.companies.search}</span>
          <input
            className="inp"
            autoFocus
            value={query}
            placeholder="123-456-78-90"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {items && <p className="hint">{t.companies.count(items.length)}</p>}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {items === null && <p className="muted">{t.loading}</p>}
      {items && items.length === 0 && (
        <div className="panel">
          <p>{t.companies.none}</p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="panel">
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.companies.name}</th>
                  <th>{t.companies.nip}</th>
                  <th>{t.companies.form}</th>
                  <th>{t.companies.city}</th>
                  <th className="wrap">{t.companies.owners}</th>
                  <th className="wrap">{t.companies.settlement}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((company) => (
                  <tr key={company.id}>
                    <td className="wrap">
                      <button type="button" className="btn ghost sm" onClick={() => onOpen(company.id)}>
                        <b>{company.name}</b>
                      </button>
                      {company.archivedAt && <span className="chip st-warn">{t.companies.archived}</span>}
                    </td>
                    <td className="mono">{nip(company.nip)}</td>
                    <td>{t.companies.forms[company.legalForm]}</td>
                    <td>{company.city ?? '—'}</td>
                    <td className="wrap">{company.owners.join(', ') || '—'}</td>
                    <td className="wrap">{settlement(company, t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/** Karta firmy: osoby, miesiące rozliczeń, korekty i ręczna korekta z uzasadnieniem. */
export function CompanyCard({
  companyId,
  t,
  locale,
  onBack,
  onOffice,
}: {
  companyId: string;
  t: Dictionary;
  locale: Locale;
  onBack: () => void;
  onOffice: (officeId: string) => void;
}) {
  const [card, setCard] = useState<Card | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ period: '', amount: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      setCard(await api.company(companyId));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.adjust(companyId, form.period.trim(), form.amount.replace(',', '.').trim(), form.note.trim());
      setForm({ period: '', amount: '', note: '' });
      setSaved(true);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!card) {
    return (
      <>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        <p className="muted">{t.loading}</p>
      </>
    );
  }

  const { company } = card;

  return (
    <>
      <div className="hd">
        <div>
          <h3>
            {company.name}
            {company.archivedAt && <span className="chip st-warn">{t.companies.archived}</span>}
          </h3>
          <p>
            <span className="mono">{nip(company.nip)}</span> · {t.companies.forms[company.legalForm]}
            {company.city ? ` · ${company.city}` : ''} · {settlement(company, t)} · {t.card.since}{' '}
            {date(company.createdAt, locale)}
          </p>
        </div>
        <div className="sp">
          <button type="button" className="btn ghost" onClick={onBack}>
            {t.card.back}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.card.saved}</b>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="hd">
          <div>
            <h4>{t.card.office}</h4>
          </div>
        </div>
        {card.offices.length === 0 ? (
          <p className="muted">{t.card.noOffice}</p>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.offices.name}</th>
                  <th>{t.offices.payer}</th>
                  <th className="wrap">{t.offices.status}</th>
                  <th>{t.offices.from}</th>
                  <th className="wrap">{t.offices.accountants}</th>
                </tr>
              </thead>
              <tbody>
                {card.offices.map((agreement) => (
                  <tr key={agreement.id}>
                    <td className="wrap">
                      <button type="button" className="btn ghost sm" onClick={() => onOffice(agreement.officeId)}>
                        <b>{agreement.officeName}</b>
                      </button>
                    </td>
                    <td>{t.offices.payers[agreement.payer]}</td>
                    <td className="wrap">
                      <AgreementStatus agreement={agreement} t={t} />
                    </td>
                    <td>{date(agreement.validFrom, locale)}</td>
                    <td className="wrap">{agreement.accountants.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <IndividualAgreements subject={{ kind: 'companies', id: companyId }} t={t} locale={locale} />

      <div className="grid two">
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.card.people}</h4>
            </div>
          </div>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.companies.name}</th>
                  <th>{t.card.role}</th>
                  <th>{t.card.from}</th>
                  <th>{t.card.to}</th>
                </tr>
              </thead>
              <tbody>
                {card.members.map((member) => (
                  <tr key={`${member.email}-${member.role}-${member.validFrom}`}>
                    <td className="wrap">
                      {member.fullName}
                      <div className="hint">{member.email}</div>
                    </td>
                    <td>{t.card.roles[member.role]}</td>
                    <td>{date(member.validFrom, locale)}</td>
                    <td>{member.validTo ? date(member.validTo, locale) : t.card.stillActive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <form className="panel" onSubmit={(event) => void save(event)}>
          <div className="hd">
            <div>
              <h4>{t.card.manual}</h4>
              <p>{t.card.manualLead}</p>
            </div>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.card.period}</span>
              <input
                className="inp mono"
                style={{ maxWidth: 140 }}
                value={form.period}
                placeholder="2026-09"
                onChange={(event) => setForm({ ...form, period: event.target.value })}
                required
              />
            </label>
            <label className="fld">
              <span>{t.card.amount}</span>
              <input
                className="inp mono"
                style={{ maxWidth: 160 }}
                value={form.amount}
                placeholder="-40,00"
                inputMode="decimal"
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
                required
              />
            </label>
          </div>
          <label className="fld">
            <span>{t.card.note}</span>
            <textarea
              className="inp"
              rows={3}
              value={form.note}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
              required
            />
          </label>
          <div className="row">
            <button type="submit" className="btn" disabled={busy || form.note.trim() === ''}>
              {t.card.save}
            </button>
          </div>
        </form>
      </div>

      <div className="panel">
        <div className="hd">
          <div>
            <h4>{t.card.months}</h4>
          </div>
        </div>
        {card.months.length === 0 ? (
          <p className="muted">{t.card.noMonths}</p>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.credits.period}</th>
                  <th>{t.card.plan}</th>
                  <th>{t.card.payer}</th>
                  <th className="n">{t.card.revenue}</th>
                  <th className="n">{t.card.fee}</th>
                  <th className="wrap">{t.card.zero}</th>
                </tr>
              </thead>
              <tbody>
                {card.months.map((month) => (
                  <tr key={month.period}>
                    <td className="mono">{month.period}</td>
                    <td>{month.plan}</td>
                    <td>{month.payer}</td>
                    <td className="n mono">{money(month.revenue, locale)}</td>
                    <td className="n mono">
                      {money(month.feeGross, locale)}
                      {Number(month.agreementFeeGross) > 0 && (
                        <div className="hint">
                          {t.card.ofWhichAgreement} {money(month.agreementFeeGross, locale)}
                        </div>
                      )}
                    </td>
                    <td className="wrap">{month.zeroReason ? t.card.zeroReasons[month.zeroReason] ?? month.zeroReason : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel">
        <div className="hd">
          <div>
            <h4>{t.card.adjustments}</h4>
          </div>
        </div>
        {card.adjustments.length === 0 ? (
          <p className="muted">{t.card.noAdjustments}</p>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.credits.period}</th>
                  <th>{t.card.kind}</th>
                  <th className="n">{t.card.amount}</th>
                  <th>{t.card.status}</th>
                  <th className="wrap">{t.card.note}</th>
                </tr>
              </thead>
              <tbody>
                {card.adjustments.map((adjustment) => (
                  <tr key={adjustment.id}>
                    <td className="mono">{adjustment.period}</td>
                    <td>{t.card.kinds[adjustment.kind]}</td>
                    <td className="n mono">{money(adjustment.amount, locale)}</td>
                    <td>
                      <span className={adjustment.status === 'REJECTED' ? 'chip st-bad' : adjustment.status === 'PENDING_APPROVAL' ? 'chip st-warn' : 'chip st-ok'}>
                        {t.card.statuses[adjustment.status]}
                      </span>
                    </td>
                    <td className="wrap">{adjustment.decisionNote ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/** Stan umowy obsługi: zgoda firmy, odmowa, zakończenie — w tej kolejności ważności. */
export function agreementStatus(agreement: Agreement, on = new Date().toISOString().slice(0, 10)): 'PENDING' | 'EFFECTIVE' | 'REJECTED' | 'ENDED' {
  if (agreement.rejectedAt) return 'REJECTED';
  if (!agreement.acceptedAt) return 'PENDING';
  if (agreement.validTo && agreement.validTo < on) return 'ENDED';
  return 'EFFECTIVE';
}

export function AgreementStatus({ agreement, t }: { agreement: Agreement; t: Dictionary }) {
  const status = agreementStatus(agreement);
  const tone = status === 'EFFECTIVE' ? 'st-ok' : status === 'PENDING' ? 'st-warn' : status === 'REJECTED' ? 'st-bad' : 'st-draft';
  return <span className={`chip ${tone}`}>{t.offices.statuses[status]}</span>;
}
